<template>
  <div class="assignment-data-view">
    <div class="outer-container">
      <div class="top-left">
        <h1 class="greeting">Hello, {{ userName }}</h1>
        <md-field class="ui-theme-3 compact sort-by-field">
          <md-select
            :value="state.sortBy"
            @input="updateSortBy"
          >
            <md-option
              v-for="option in sortByOptions"
              :key="option.key"
              :value="option.key"
            >
              {{ option.label }}
            </md-option>
          </md-select>
        </md-field>
      </div>
      <div class="placeholder" />
      <md-content class="scroll-content md-scrollbar gray-3 bg">
        <div class="md-layout">
          <div class="md-layout-item">
            <div class="header md-elevation-3">Pending</div>
            <md-progress-bar v-if="loading" md-mode="indeterminate" />
            <assignment-card
              v-for="assign in pending"
              :key="assign.id"
              :assignment="assign"
              @selected="selectAssignment(assign)"
              @startTask="asnmt => startTask(asnmt)"
              @deleteTask="asnmt => deleteTask(asnmt)"
            />
          </div>
          <div class="md-layout-item">
            <div class="header md-elevation-3">In Progress</div>
            <md-progress-bar v-if="loading" md-mode="indeterminate" />
            <assignment-card
              v-for="assign in inProgress"
              :key="assign.id"
              :assignment="assign"
              @selected="selectAssignment(assign)"
              @completeTask="asnmt => completeTask(asnmt)"
              @deleteInProgress="asnmt => deleteInProgress(asnmt)"
            />
          </div>
          <div class="md-layout-item">
            <div class="header md-elevation-3">Complete</div>
            <md-progress-bar v-if="loading" md-mode="indeterminate" />
            <assignment-card
              v-for="assign in complete"
              :key="assign.id"
              :assignment="assign"
              @selected="selectAssignment(assign)"
              @removeTask="asnmt => removeTask(asnmt)"
            />
          </div>
        </div>
      </md-content>
      <div class="details-container">
        <assignment-details
          v-if="state.selectedAssignment"
          :assignment="state.selectedAssignment"
          :user="user"
          :org-data="orgData"
        />
      </div>
    </div>
  </div>
</template>

<script>
import intersection from 'lodash/intersection';
import keyBy from 'lodash/keyBy';
import { userLabel } from '~lib/common/utils/labels';

import AssignmentCard from './AssignmentCard';
import AssignmentDetails from '../AssignmentList/AssignmentDetails';

const sortByOptions = [
  { key: 'name', label: 'Sort by Name' },
  { key: 'date', label: 'Sort by Date' },
];

export default {
  name: 'assignment-list',
  components: {
    AssignmentCard,
    AssignmentDetails,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
    },
    data: {
      type: Array,
      default: () => [],
    },
    orgData: {
      type: Object,
      default: undefined,
    },
  },
  data() {
    return {
      state: {
        sortBy: 'name',
        selectedAssignment: null,
      },
      sortByOptions
    };
  },
  computed: {
    assignmentsWithCreator() {
      const assignments = this.data
        .filter(assignment => {
          return this.hasAccess(assignment) // hide assignments that are only available due to timed_access
        })
        .map(assignment => {
          return {
            ...assignment,
            createdByLabel: this.usersById && assignment.access ? userLabel(this.usersById[assignment.access.owner]) : null
          };
        });
      return assignments.sort((a, b) => {
        if (this.state.sortBy === 'name') {
          return a.name.localeCompare(b.name);
        } else if (this.state.sortBy === 'date') {
          return new Date(a.startTime) - new Date(b.startTime);
        }
        return 0;
      });
    },
    userName() {
      return userLabel(this.user);
    },
    pending() {
      const now = new Date().toJSON();
      return this.assignmentsWithCreator.filter(a => a.startTime > now);
    },
    inProgress() {
      const now = new Date().toJSON();
      return this.assignmentsWithCreator.filter(a => a.startTime <= now && a.endTime >= now);
    },
    complete() {
      const now = new Date().toJSON();
      return this.assignmentsWithCreator.filter(a => a.endTime < now && a.status !== 'REMOVED');
    },
    usersById() {
      return this.orgData ? keyBy(this.orgData.users, 'user_id') : [];
    }
  },
  methods: {
    selectAssignment(assignment) {
      this.state.selectedAssignment = assignment;
    },
    startTask(assignment) {
      this.$emit('startTask', assignment);
    },
    deleteTask(assignment) {
      this.$emit('deleteTask', assignment);
      this.selectAssignment(undefined);
    },
    completeTask(assignment) {
      this.$emit('completeTask', assignment);
    },
    removeTask(assignment) {
      this.$emit('removeTask', assignment);
      this.selectAssignment(undefined);
    },
    deleteInProgress(assignment) {
      this.$emit('deleteInProgress', assignment);
      this.selectAssignment(undefined);
    },
    hasAccess(assignment) {
      const { access } = assignment;
      let result = false;
      if (assignment) {
        const userAccess = access.read.users && access.read.users.includes(this.user.user_id);
        const groupAccess = access.read.groups && intersection(access.read.groups, this.user.groups).length > 0;
        if (userAccess || groupAccess) {
          result = true;
        }
      }
      return result;
    },
    updateSortBy(value) {
      console.log('updateSortBy', value);
      this.state.sortBy = value;
    },
  },
};
</script>

<style scoped>
.assignment-data-view {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.outer-container {
  padding: 20px 25px;
  height: 100%;
  display: grid;
  grid-template-columns: 60% 40%; /* Left column 60%, right column 40% */
  grid-template-rows: auto 1fr;
  width: 100%;
}

.top-left {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.placeholder {
  height: 0;
  padding: 0;
  margin: 0;
}

.sort-by-field {
  width: auto;
}

.details-container {
  height: 100%;
  width: 100%;
  padding-left: 15px;
  box-sizing: border-box;
}

.greeting {
  text-decoration: underline;
  text-decoration-color: #0870B0;
  margin-bottom: 40px;
}

.scroll-content {
  overflow: auto;
  height: 100%;
  flex: 3;
}

.md-layout {
  width: 100%;
  min-height: 100%;
}

.md-layout-item {
  background: #e3e3e3;
  border-radius: 8px;
  padding: 20px;
}

.md-layout-item:not(:last-child) {
  margin-right: 10px;
}

.header {
  background-color: white;
  border-radius: 5px;
  font-size: 16px;
  height: 40px;
  line-height: 40px;
  vertical-align: center;
  text-align: center;
  text-transform: uppercase;
  margin-bottom: 20px;
}
</style>